import { UseMutationOptions, useIsMutating, useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { bumpPluginCommitSeq, PluginFlushOptions, registerPluginFlush } from '@/lib/plugins/plugin-flush-registry'
import { checkPluginReadiness } from '@/lib/plugins/plugin-readiness'

type Args<TData, TError> = UseMutationOptions<TData, TError, void, unknown> & {
  flushId: string
  scopeKey?: string
  isDirty?: () => boolean
  isReady?: () => boolean
  notReadyMessage?: string
  // Source versions (query dataUpdatedAt values) this plugin's isDirty and
  // mutationFn closures were built from. The flush requires these to be stable
  // across consecutive clean passes before it trusts convergence.
  fingerprint?: () => unknown
}

export function usePluginMutation<TData = unknown, TError = Error>({
  flushId,
  scopeKey,
  isDirty,
  isReady,
  notReadyMessage = 'Plugin data is still loading',
  fingerprint,
  ...mutationOptions
}: Args<TData, TError>) {
  const mutation = useMutation<TData, TError, void, unknown>(mutationOptions)
  const { mutateAsync } = mutation
  const isDtoolsSiImporting = useIsMutating({ mutationKey: ['dtools-si', 'import-project'] }) > 0

  const pendingRef = useRef<Promise<TData> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isDirtyRef = useRef(isDirty)
  const isReadyRef = useRef(isReady)
  const notReadyMessageRef = useRef(notReadyMessage)
  const scopeKeyRef = useRef(scopeKey)
  const fingerprintRef = useRef(fingerprint)
  isDirtyRef.current = isDirty
  isReadyRef.current = isReady
  notReadyMessageRef.current = notReadyMessage
  scopeKeyRef.current = scopeKey
  fingerprintRef.current = fingerprint

  // Signals the flush that a commit landed, so refs above now reflect the
  // latest cache data this component subscribes to. Deliberately no dep array.
  useEffect(() => {
    bumpPluginCommitSeq()
  })

  const fire = useCallback(async (options?: PluginFlushOptions): Promise<boolean> => {
    if (isDtoolsSiImporting) return false
    if (
      !checkPluginReadiness({
        isReady: isReadyRef.current ? isReadyRef.current() : true,
        requireReady: options?.requireReady,
        notReadyMessage: notReadyMessageRef.current,
      })
    ) {
      return false
    }
    if (pendingRef.current) {
      try {
        await pendingRef.current
      } catch {
        /* already surfaced by originating caller */
      }
      // After in-flight work resolves, re-check dirtiness in case state
      // diverged again during the prior mutation. Recurse to either fire a
      // fresh write or report no-op cleanly.
      if (isDirtyRef.current && !isDirtyRef.current()) return true
      return fire(options)
    }
    if (isDirtyRef.current && !isDirtyRef.current()) return false
    try {
      pendingRef.current = mutateAsync()
      await pendingRef.current
      return true
    } finally {
      pendingRef.current = null
    }
  }, [isDtoolsSiImporting, mutateAsync])

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (isDtoolsSiImporting) cancel()
  }, [isDtoolsSiImporting, cancel])

  const schedule = useCallback(
    (delay: number) => {
      if (isDtoolsSiImporting) {
        cancel()
        return
      }
      cancel()
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        fire().catch(() => {
          /* ambient path: plugin onError already logs */
        })
      }, delay)
    },
    [cancel, fire, isDtoolsSiImporting]
  )

  useEffect(() => {
    if (!scopeKey) return
    const registeredScopeKey = scopeKey
    const flush = async (options?: PluginFlushOptions) => {
      if (scopeKeyRef.current !== registeredScopeKey) {
        throw new Error('Quote changed while calculations were syncing.')
      }
      cancel()
      return fire(options)
    }
    return registerPluginFlush(flushId, registeredScopeKey, flush, () => fingerprintRef.current?.())
  }, [flushId, scopeKey, cancel, fire])

  useEffect(() => {
    return () => cancel()
  }, [cancel])

  return { ...mutation, fire, schedule, cancel }
}
